import React, { useState } from 'react';
import useWizardTree from '../../../../Hooks/useWizardTree';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import MagicForm from '../../../../Components/Form/MagicForm';
import useDynamicWizard from '../../Hooks/useDynamicWizard';
import { ItemScheme } from '../../../../Components/Form/MagicFormItem';
// @ts-ignore
import styles from './DynamicForm.module.scss';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useTranslation from '../../../../Hooks/useTranslation';
import useNotification from '../../../../Hooks/useNotification.hook';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import { formatObjectForQuery } from '../../../../Services/Api/FormatObjectForQuery';
import { PlainTextViewerTemplates } from '../MagicFormPlainTextViewer/PlainTextViewerTemplates';

interface Props {
	wizardKey: string;
	setIsLoading: (isLoading: boolean) => void;
	params?: Record<string, any>;
	onSuccess?: (formKey: string) => void;
}

interface WizardSettings {
	showCharCounter?: boolean;
	disabled?: boolean;
	submitButtonTitle?: string;
}

const DynamicForm: React.FC<Props> = ({
	wizardKey,
	params,
	setIsLoading,
	onSuccess,
}) => {
	const [scheme, setScheme] = useState<ItemScheme[]>([]);
	const [defaultValues, setDefaultValues] = useState<object>({});
	const [isWizardLoading, setIsWizardLoading] = useState(false);
	const [wizardSettings, setWizardSettings] = useState<WizardSettings>({});

	const { generateDefaultValues, prepareScheme } = useDynamicWizard();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	const plainTextViewerTemplate = PlainTextViewerTemplates[wizardKey];

	const { getWizardStructure } = useWizardTree({
		wizardKey,
		params,
	});

	const loadWizard = async () => {
		setIsWizardLoading(true);
		const wizard = await getWizardStructure();
		const preparedScheme = prepareScheme(wizard.children);
		setScheme(preparedScheme);
		setDefaultValues(generateDefaultValues(preparedScheme));
		setWizardSettings(wizard?.settings);
		setIsWizardLoading(false);
	};

	useEffectAsync(async () => {
		await loadWizard();
	}, [wizardKey]);

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		try {
			const queryParams = params ? formatObjectForQuery(params) : '';
			const response = await apiClient.post(
				`wizard/${wizardKey}/submit?${queryParams}`,
				values,
			);
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('base.saved'),
				});
				await loadWizard();
				onSuccess?.(wizardKey);
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isWizardLoading) {
		return <Loading />;
	}

	return (
		<ScrollContainer>
			<div className={styles.wrapper}>
				<MagicForm
					formScheme={scheme}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
					showCharCounter={wizardSettings?.showCharCounter || true}
					disableSubmit={wizardSettings?.disabled || false}
					submitText={wizardSettings?.submitButtonTitle}
					readOnly={wizardSettings?.disabled || false}
					plainTextViewTemplate={plainTextViewerTemplate}
				/>
			</div>
		</ScrollContainer>
	);
};

export default DynamicForm;
